import {
	AddSubjectsRequest,
	Configuration,
	RemoveSubjectRequest,
	SearchEsponentiDto,
	SearchSubjectRequest,
	SendIvitationsRequest,
	SoggettiPraticaDto,
	SoggettoDto,
	SubjectApi,
	SubjectRequest,
	SubjectsRequest,
	TitolareDto,
	UpdateSubjectRequest,
} from "@api/client";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { appConfig } from "core/app.config";
import { ThunkApiConfig } from "core/store";
import getConfiguration, { errorHandler } from "core/store/config";

enum SubjectReducer {
	getDetails = "[SUBJECT] Get details",
	setSubjects = "[SUBJECT] Set subjects",
	setSubjectDetail = "[SUBJECT] Set subject detail",
	updateSubject = "[SUBJECT] Update subject",
	removeSubject = "[SUBJECT] Remove subject",
	searchSubject = "[SUBJECT] Search subject",
	sendInvitation = "[SUBJECT] Send invitation",
	setTitolariEffettiviAdded = "[SUBJECT] Set titolari effettivi added",
	getSubject = "[SUBJECT] Get Subject",
	setIdLegaleRappresentante = "[SUBJECT] Set ID Legale Rapprsentante",
	cleanData = "[SUBJECT] Clean Subject Data",
}

export const clearSubjects = createAction<void>(SubjectReducer.cleanData);

export const subjectSetSubjects = createAction<SoggettiPraticaDto>(SubjectReducer.setSubjects);

export const subjectSetSubjectDetail = createAction<SoggettoDto>(SubjectReducer.setSubjectDetail);

export const subjectSetTitolariEffettiviAdded = createAction<any>(
	SubjectReducer.setTitolariEffettiviAdded
);

export const subjectSetIdLegaleRappresentante = createAction<string>(
	SubjectReducer.setIdLegaleRappresentante
);

export const subjectGetDetails = createAsyncThunk<
	SoggettiPraticaDto | undefined,
	void,
	ThunkApiConfig
>(SubjectReducer.getDetails, async (_, { dispatch, extra, getState }) => {
	try {
		let idPratica = getState().pratica?.idPratica;
		let idCliente = getState().company?.idCliente;

		if (!idPratica) throw "ID Pratica non presente";
		if (!idCliente) throw "ID Cliente non presente";

		const request: SubjectsRequest = {
			idPratica,
			idCliente,
		};

		return await new SubjectApi(getConfiguration(Configuration, extra, getState))
			.subjects(request)
			.then((res) => {
				if (res) dispatch(subjectSetSubjects(res));
				return res;
			});
	} catch (e) {
		errorHandler(e, dispatch);
	}
});

export const subjectAddSubject = createAsyncThunk<
	SoggettiPraticaDto | undefined,
	string | TitolareDto[],
	ThunkApiConfig
>(SubjectReducer.updateSubject, async (data, { dispatch, extra, getState }) => {
	try {
		let idPratica = getState().pratica?.idPratica;
		let idCompany = getState().company?.idCompany;
		let idCliente = getState().company?.idCliente;

		if (!idPratica) throw "ID Pratica non presente";
		if (!idCompany) throw "ID Company non presente";
		if (!idCliente) throw "ID Cliente non presente";

		let request: AddSubjectsRequest = {
			soggettiPraticaInput: {
				idPratica,
				idCliente,
				idSoggetto: idCompany,
			},
		};

		if (typeof data == "string") request.soggettiPraticaInput.idLegaleRapp = data;
		else request.soggettiPraticaInput.elencoTitolariEffettivi = new Set<TitolareDto>(data);

		return await new SubjectApi(getConfiguration(Configuration, extra, getState)).addSubjects(
			request
		);
	} catch (e) {
		errorHandler(e, dispatch);
	}
});

export const subjectUpdateSubject = createAsyncThunk<
	SoggettoDto | undefined,
	{ subject: SoggettoDto; sendNotification?: boolean; skipSaveStepStatus?: boolean },
	ThunkApiConfig
>(
	SubjectReducer.updateSubject,
	async ({ subject, sendNotification, skipSaveStepStatus }, { dispatch, extra, getState }) => {
		try {
			let idPratica = getState().pratica?.idPratica;

			if (!idPratica) throw "ID Pratica non presente";

			const reqeust: UpdateSubjectRequest = {
				idPratica,
				soggettoDto: subject,
				sendNotification,
				skipSaveStepStatus,
			};

			return await new SubjectApi(getConfiguration(Configuration, extra, getState))
				.updateSubject(reqeust)
				.then((res) => {
					if (res) dispatch(subjectSetSubjectDetail(res));
					return res;
				});
		} catch (e) {
			errorHandler(e, dispatch);
		}
	}
);

export const subjectRemoveSubject = createAsyncThunk<
	boolean | SoggettoDto | undefined,
	{ subject: SoggettoDto },
	ThunkApiConfig
>(SubjectReducer.removeSubject, async ({ subject }, { dispatch, extra, getState }) => {
	try {
		let idPratica = getState().pratica?.idPratica;
		let idCliente = getState().company?.idCliente;

		if (!idPratica) throw "ID Pratica non presente";
		if (!idCliente) throw "ID Cliente non presente";

		let request: RemoveSubjectRequest = {
			idPratica,
			idCliente,
			soggettoDto: subject,
		};

		return await new SubjectApi(getConfiguration(Configuration, extra, getState))
			.removeSubject(request)
			.then((res) => {
				// console.log(res);
				return res;
			});
	} catch (e) {
		errorHandler(e, dispatch);
	}
});

export const subjectSearchSubject = createAsyncThunk<
	SearchEsponentiDto | undefined,
	boolean,
	ThunkApiConfig
>(SubjectReducer.searchSubject, async (isOwners, { dispatch, extra, getState }) => {
	try {
		let idPratica = getState().pratica?.idPratica;

		if (!idPratica) throw "ID Pratica non presente";

		let request: SearchSubjectRequest = {
			idPratica,
			isOwners,
			isTest: appConfig.isTest,
		};

		return await new SubjectApi(getConfiguration(Configuration, extra, getState)).searchSubject(
			request
		);
	} catch (e) {
		errorHandler(e, dispatch);
	}
});

export const subjectSendInvitation = createAsyncThunk<
	object | undefined,
	SendIvitationsRequest,
	ThunkApiConfig
>(SubjectReducer.sendInvitation, async (request, { dispatch, extra, getState }) => {
	try {
		return await new SubjectApi(getConfiguration(Configuration, extra, getState)).sendIvitations(
			request
		);
	} catch (e) {
		errorHandler(e, dispatch);
	}
});

export const subjectGetSubject = createAsyncThunk<
	SoggettoDto | undefined,
	SubjectRequest,
	ThunkApiConfig
>(SubjectReducer.getSubject, async (request, { dispatch, extra, getState }) => {
	try {
		return await new SubjectApi(getConfiguration(Configuration, extra, getState)).subject(request);
	} catch (error) {
		errorHandler(error, dispatch);
	}
});
